import { useEffect, useRef, useState } from "react";
import API from "../axios";
import { Col, Row } from "react-bootstrap";
import ColumnToggleDialog from "../components/PurchaseOrderPivotView/ColumnToggleDialog";
import CustomerInfoTable from "../components/CustomerShippingView/CustomerInfoTable";
import OrderStatusView from "../components/CustomerShippingView/OrderStatusView";
import DeliveryDashboard from "../components/Dashboard/DeliveryDashboardStatus";

const DeliveryList = () => {
    /**
     * Datas
     */
    const [shippingData, setShippingData] = useState([]);
    const [dashboardData, setDashboardData] = useState();
    const [dateColumns, setDateColumns] = useState([]);

    /**
     * State controllers
     */
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    /**
     * Search suggestions
     */
    const suggestions = ["SO Number"];

    /**
     * For daily view
     */
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    // For weekly and montly view
    const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());

    const [currentStartDate, setCurrentStartDate] = useState("");
    const [currentEndDate, setCurrentEndDate] = useState("");

    /**
     * UI state controllers
     */
    const [tableView, setTableView] = useState<
        "timeline" | "stage" | "dashboard"
    >("stage");
    const [viewType, setViewType] = useState<"daily" | "weekly" | "monthly">("daily");
    const [openedSoNumber, setOpenedSoNumber] = useState(false);

    /**
     * Input field models
     */
    const [search, setSearch] = useState("");
    const [soName, setSoName] = useState("");

    /**
     * To sync two tables left and right
     */
    const leftScrollRef = useRef<HTMLDivElement | null>(null);
    const rightScrollRef = useRef<HTMLDivElement | null>(null);
    const isSyncing = useRef(false);

    const [shippingInfoColumns, setShippingInfoColumns] = useState([
        {
            name: "SO NUMBER",
            visiable: true,
        },
        {
            name: "QTY ORDERED",
            visiable: true,
        },
        {
            name: "QTY SHIPPED",
            visiable: true,
        },
        {
            name: "QTY DELIVERED",
            visiable: true,
        },
        {
            name: "Next Step",
            visiable: true,
        },
    ]);

    const [insideInfoColumns, setInsideInfoColumns] = useState([
        {
            name: "Product",
            visiable: true,
        },
        {
            name: "Qty Ordered",
            visiable: true,
        },
        {
            name: "Qty Shipped",
            visiable: true,
        },
        {
            name: "Qty Received",
            visiable: true,
        },
    ]);

    function getMonthDates(
        targetMonth: number,
        targetYear: number
    ): { startDate: string; endDate: string } {
        const startDate = new Date(targetYear, targetMonth - 1, 1);
        const endDate = new Date(targetYear, targetMonth, 0);

        const formatDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };

        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
        };
    }

    async function handleSearch(item?: string) {
        setIsSearchOpen(false);
        setLoading(true);

        try {
            let filter: any = {};

            if (item) {
                if (item == "SO NUMBER") filter.name = search.trim();

                let result: any;
                result = await API.getDeliveryList(
                    undefined,
                    undefined,
                    filter
                );

                setDateColumns(result.dates);
                setShippingData(result.data);
                setDashboardData(result.dashboardData);
            } else {
                if (soName) filter.name = soName.trim();

                let result: any;
                if (soName) {
                    result = await API.getShippingList(
                        undefined,
                        undefined,
                        filter
                    );
                } else {
                    // Use appropriate date range based on view type
                    let dates;
                    if (viewType === "weekly" || viewType == "monthly") {
                        dates = {
                            startDate: `${monthlyYear}-01-01`,
                            endDate: `${monthlyYear}-12-31`,
                        };
                    } else {
                        dates = getMonthDates(currentMonth, currentYear);
                    }

                    /**
                     * Sync the dates to component state
                     */
                    setCurrentStartDate(dates.startDate);
                    setCurrentEndDate(dates.endDate);

                    result = await API.getShippingList(
                        dates.startDate,
                        dates.endDate
                    );
                }

                setDateColumns(result.dates);
                setShippingData(result.data);
                setDashboardData(result.dashboardData);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    }

    async function handleClearSearch() {
        setSoName("");

        if (viewType === "monthly" || viewType == "weekly") {
            loadShippingDataMonthly();
        } else {
            loadShippingData();
        }
    }

    async function loadShippingData(
        month = currentMonth,
        year = currentYear,
        params: any = {}
    ) {
        setLoading(true);

        const dates = getMonthDates(month, year);

        setCurrentStartDate(dates.startDate);
        setCurrentEndDate(dates.endDate);        

        try {
            const result: any = await API.getDeliveryList(
                dates.startDate,
                dates.endDate,
                params
            );

            setDateColumns(result.dates);
            setShippingData(result.data);
            setDashboardData(result.dashboardData);
        } catch (error) {
            console.error("Error loading shipping data:", error);
        } finally {
            setLoading(false);
        }
    }

    async function loadShippingDataMonthly(year?: number, params: any = {}) {
        setLoading(true);

        const useYear = year ?? monthlyYear;

        try {
            // For monthly view, use the entire year
            const startDate = `${useYear}-01-01`;
            const endDate = `${useYear}-12-31`;

            setCurrentStartDate(startDate);
            setCurrentEndDate(endDate);

            const result: any = await API.getDeliveryList(
                startDate,
                endDate,
                params
            );

            setDateColumns(result.dates);
            setShippingData(result.data);
            setDashboardData(result.dashboardData.data);
        } catch (error) {
            console.error("Error loading monthly shipping data:", error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }

    const handlePreviousMonth = () => {
        let newMonth = currentMonth - 1;
        let newYear = currentYear;

        if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }

        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
        loadShippingData(newMonth, newYear);
    };

    const handleNextMonth = () => {
        let newMonth = currentMonth + 1;
        let newYear = currentYear;

        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        }

        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
        loadShippingData(newMonth, newYear);
    };

    const getMonthYearDisplay = () => {
        const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        return `${monthNames[currentMonth - 1]} ${currentYear}`;
    };

    const handleScroll = (source: any, target: any) => {
        if (isSyncing.current) return;

        isSyncing.current = true;

        if (source.current && target.current) {
            target.current.scrollTop = source.current.scrollTop;
        }

        requestAnimationFrame(() => {
            isSyncing.current = false;
        });
    };

    const handleViewTypeChange = (
        newViewType: "daily" | "weekly" | "monthly"
    ) => {
        setViewType(newViewType);

        if (newViewType === "monthly" || viewType == "weekly") {
            // Set current year for monthly view
            const currentYear = new Date().getFullYear();
            setMonthlyYear(currentYear);
            loadShippingDataMonthly(currentYear);
        } else {
            loadShippingData();
        }
    };

    const saveColumnStates = function () {
        localStorage.setItem(
            "delivery_columnState",
            JSON.stringify(shippingInfoColumns)
        );
    };

    const saveViewState = function () {
        if (tableView) {
            localStorage.setItem("delivery_view", tableView);
        }
    };

    const saveViewType = function () {
        if (viewType) {
            localStorage.setItem("delivery_viewType", viewType);
        }
    };

    const loadPreviousColumnStates = function () {
        const cleared = localStorage.getItem("cleared4");
        if (!cleared) {
            localStorage.removeItem("delivery_columnState");
            localStorage.setItem("cleared3", "true");
        }

        const previousState = localStorage.getItem("delivery_columnState");
        if (previousState) {
            setShippingInfoColumns(JSON.parse(previousState));
        }
    };

    const loadViewState = function () {
        const previousState = localStorage.getItem("delivery_view");
        if (previousState) {
            setTableView(previousState as "timeline" | "stage");
        } else {
            setTableView("stage");
        }
    };

    const loadViewType = function () {
        const previousState = localStorage.getItem("delivery_viewType");
        if (previousState) {
            setViewType(previousState as "daily" | "weekly" | "monthly");
        } else {
            setViewType("daily");
        }
    };

    // Monthly view navigation
    const handlePreviousYear = () => {
        const newYear = monthlyYear - 1;
        setMonthlyYear(newYear);
        loadShippingDataMonthly(newYear);
    };

    const handleNextYear = () => {
        const newYear = monthlyYear + 1;
        setMonthlyYear(newYear);
        loadShippingDataMonthly(newYear);
    };

    function isDateOlderThanToday(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return inputDate < today;
    }

    function isDateInTwoDays(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();
        const twoDaysFromNow = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        twoDaysFromNow.setHours(0, 0, 0, 0);
        twoDaysFromNow.setDate(today.getDate() + 2);

        return inputDate.getTime() === twoDaysFromNow.getTime();
    }

    function getNextStepColor(date: string | undefined) {
        if (!date) {
            return "#d2d2d2ff";
        }

        if (isDateOlderThanToday(date)) {
            return "#fa7f52ff";
        } else if (isDateInTwoDays(date)) {
            return "#e0f052ff";
        } else {
            return "#43bd38ff";
        }
    }

    useEffect(() => {
        loadPreviousColumnStates();
        loadViewType();
        loadViewState();

        // Load daily data by default
        loadShippingData();
    }, []);

    useEffect(() => {
        if (!search) {
            handleClearSearch();
        }
    }, [search]);

    useEffect(() => {
        if (viewType === "monthly" || viewType == "weekly") {
            loadShippingDataMonthly();
        }

        saveViewType();
    }, [viewType]);

    useEffect(() => {
        saveViewState();
    }, [tableView]);

    return (
        <div className="min-vh-100" style={{ overflow: "hidden" }}>
            <div className="container-fluid" style={{ marginTop: 100 }}>
                {/* Header */}
                <Row>
                    {/* Title */}
                    <Col cols="4" className="d-flex align-items-center gap-2">
                        <a
                            className="btn btn-outline-black btn-md mx-1"
                            href={`/`}
                        >
                            <i className="bi bi-chevron-left fs-4"></i>
                        </a>
                        <h3 className="fw-medium text-primary">
                            Delivery Tracking
                        </h3>
                    </Col>
                    <Col
                        cols="8"
                        className=" d-flex align-items-center justify-content-end gap-2"
                    >
                        <div className="col-md-6 p-0">
                            <div className="position-relative">
                                {/* Search field */}
                                <div className="input-group">
                                    <input
                                        style={{
                                            maxHeight: 30,
                                        }}
                                        type="text"
                                        className="form-control"
                                        placeholder="Search..."
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        onFocus={() => setIsSearchOpen(true)}
                                        onBlur={() =>
                                            setTimeout(() => {
                                                setIsSearchOpen(false);
                                            }, 1000)
                                        }
                                    />
                                    <button
                                        className="btn btn-primary"
                                        type="button"
                                        style={{
                                            maxHeight: 30,
                                            fontSize: 10,
                                        }}
                                    >
                                        <i className="bi bi-search"></i>
                                    </button>
                                </div>

                                {/* Search field dialog */}
                                {search && isSearchOpen && (
                                    <div
                                        className="dropdown-menu show w-100 position-absolute"
                                        style={{ top: "100%", zIndex: 1000 }}
                                    >
                                        {suggestions.map((item, index) => (
                                            <button
                                                key={index}
                                                className="dropdown-item"
                                                style={{
                                                    fontSize: 15,
                                                }}
                                                onClick={() =>
                                                    handleSearch(item)
                                                }
                                            >
                                                <b>{item}</b> : {search}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <select
                            className="form-select form-select-sm mx-1"
                            style={{ width: "auto", display: "inline-block" }}
                            value={viewType}
                            onChange={(e) =>
                                handleViewTypeChange(
                                    e.target.value as
                                        | "daily"
                                        | "weekly"
                                        | "monthly"
                                )
                            }
                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>

                        {/* Dashboard views */}
                        <button
                            className={`btn btn-sm ${
                                tableView == "stage"
                                    ? "btn-secondary text-white"
                                    : "btn-outline-secondary"
                            }`}
                            onClick={() => setTableView("stage")}
                        >
                            <i className="bi bi-check-circle"></i>
                        </button>
                        <button
                            className={`btn btn-sm ${
                                tableView == "dashboard"
                                    ? "btn-secondary text-white"
                                    : "btn-outline-secondary"
                            }`}
                            onClick={() => setTableView("dashboard")}
                        >
                            <i className="bi bi-speedometer"></i>
                        </button>
                        <div
                            style={{
                                height: "50%",
                                width: 1,
                                backgroundColor: "#cacacaff",
                            }}
                        ></div>

                        {/* Columns toggle */}
                        <button
                            className="btn btn-sm btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#columnToggle"
                        >
                            <i className="bi bi-columns"></i>
                        </button>

                        {/* Date view and control */}
                        {viewType === "daily" ? (
                            <>
                                <button
                                    onClick={handlePreviousMonth}
                                    className="btn btn-flat"
                                    style={{ borderRadius: "8px" }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <span
                                    style={{
                                        fontSize: "1rem",
                                        fontWeight: "600",
                                        color: "#495057",
                                        minWidth: "150px",
                                        textAlign: "center",
                                    }}
                                >
                                    {getMonthYearDisplay()}
                                </span>
                                <button
                                    onClick={handleNextMonth}
                                    className="btn btn-flat"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "0.5rem 1rem",
                                    }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </>
                        ) : viewType === "weekly" ? (
                            <>
                                <button
                                    onClick={handlePreviousYear}
                                    className="btn btn-flat"
                                    style={{ borderRadius: "8px" }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <span
                                    style={{
                                        fontSize: "1rem",
                                        fontWeight: "600",
                                        color: "#495057",
                                        minWidth: "150px",
                                        textAlign: "center",
                                    }}
                                >
                                    {monthlyYear}
                                </span>
                                <button
                                    onClick={handleNextYear}
                                    className="btn btn-flat"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "0.5rem 1rem",
                                    }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </>
                        ) : (
                            // Monthly view navigation
                            <>
                                <button
                                    onClick={handlePreviousYear}
                                    className="btn btn-flat"
                                    style={{ borderRadius: "8px" }}
                                    disabled={loading}
                                    title="Previous Year"
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <span
                                    style={{
                                        fontSize: "1.2rem",
                                        fontWeight: "700",
                                        color: "#495057",
                                        minWidth: "120px",
                                        textAlign: "center",
                                    }}
                                >
                                    {monthlyYear}
                                </span>
                                <button
                                    onClick={handleNextYear}
                                    className="btn btn-flat"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "0.5rem 1rem",
                                    }}
                                    disabled={loading}
                                    title="Next Year"
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </>
                        )}
                    </Col>
                </Row>

                {/* Table body */}
                <div
                    className="card"
                    style={{
                        marginTop: "1rem",
                        marginBottom: 40,
                        borderWidth: 1,
                        borderColor: "#d8d8d8ff",
                    }}
                >
                    {loading ? (
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ minHeight: "70vh" }}
                        >
                            <div className="text-center">
                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                    style={{
                                        width: "3rem",
                                        height: "3rem",
                                    }}
                                >
                                    <span className="visually-hidden">
                                        Loading...
                                    </span>
                                </div>
                                <p className="mt-3 text-muted">
                                    Loading shipping data...
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                style={{
                                    position: "relative",
                                    overflowY: "hidden",
                                    overflowX: "auto",
                                    width: "100%",
                                }}
                            >
                                {tableView == "stage" && (
                                    <div
                                        style={{
                                            position: "relative",
                                            overflowY: "hidden",
                                            overflowX: "auto",
                                            width: "100%",
                                        }}
                                    >
                                        <table
                                            className="table table-hover mb-0"
                                            style={{
                                                borderRight:
                                                    "1px solid #efefef",
                                                width: "100%",
                                                minWidth: "100%",
                                            }}
                                        >
                                            {/* Table Header */}
                                            <thead
                                                style={{
                                                    position: "sticky",
                                                    top: 0,
                                                    backgroundColor: "#0d6efd",
                                                    color: "white",
                                                    zIndex: 10,
                                                }}
                                            >
                                                <tr>
                                                    <th
                                                        className="fw-semibold text-uppercase"
                                                        style={{
                                                            fontSize: "0.7rem",
                                                            color: "#6c757d",
                                                            padding: "0.6rem",
                                                            borderBottom:
                                                                "2px solid #dee2e6",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        So Name
                                                    </th>
                                                    <th
                                                        className="fw-semibold text-uppercase"
                                                        style={{
                                                            fontSize: "0.7rem",
                                                            color: "#6c757d",
                                                            padding: "0.6rem",
                                                            borderBottom:
                                                                "2px solid #dee2e6",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        QTY ORDERED
                                                    </th>
                                                    <th
                                                        className="fw-semibold text-uppercase"
                                                        style={{
                                                            fontSize: "0.7rem",
                                                            color: "#6c757d",
                                                            padding: "0.6rem",
                                                            borderBottom:
                                                                "2px solid #dee2e6",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        QTY SHIPPED
                                                    </th>
                                                    <th
                                                        className="fw-semibold text-uppercase"
                                                        style={{
                                                            fontSize: "0.7rem",
                                                            color: "#6c757d",
                                                            padding: "0.6rem",
                                                            borderBottom:
                                                                "2px solid #dee2e6",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        QTY RECEIVED
                                                    </th>
                                                    <th
                                                        className="fw-semibold text-uppercase"
                                                        style={{
                                                            fontSize: "0.7rem",
                                                            color: "#6c757d",
                                                            padding: "0.6rem",
                                                            borderBottom:
                                                                "2px solid #dee2e6",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        NEXT STEP
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {shippingData.map(
                                                    (soRecord: any) => (
                                                        <>
                                                            <tr
                                                                style={{
                                                                    borderBottom:
                                                                        "1px solid #f0f0f0",
                                                                    height: 50,
                                                                    fontSize: 13,
                                                                    cursor: "pointer",
                                                                }}
                                                                onClick={() => {
                                                                    setOpenedSoNumber(
                                                                        openedSoNumber ==
                                                                            soRecord.name
                                                                            ? false
                                                                            : soRecord.name
                                                                    );
                                                                }}
                                                            >
                                                                <td
                                                                    style={{
                                                                        fontWeight:
                                                                            "500",
                                                                        color: "#495057",
                                                                        backgroundColor:
                                                                            openedSoNumber ===
                                                                            soRecord.name
                                                                                ? "#a8d5f0ff"
                                                                                : "#fff",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display:
                                                                                "flex",
                                                                            flexDirection:
                                                                                "row",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            overflow:
                                                                                "hidden",
                                                                            alignItems:
                                                                                "center",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        {soRecord.name ==
                                                                        openedSoNumber ? (
                                                                            <i
                                                                                className="bi bi-dash mx-2"
                                                                                style={{
                                                                                    fontSize: 14,
                                                                                }}
                                                                            ></i>
                                                                        ) : (
                                                                            <i
                                                                                className="bi bi-plus mx-2"
                                                                                style={{
                                                                                    fontSize: 14,
                                                                                }}
                                                                            ></i>
                                                                        )}
                                                                        {
                                                                            soRecord.name
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        fontWeight:
                                                                            "500",
                                                                        color: "#495057",
                                                                        backgroundColor:
                                                                            openedSoNumber ===
                                                                            soRecord.name
                                                                                ? "#a8d5f0ff"
                                                                                : "#fff",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display:
                                                                                "flex",
                                                                            flexDirection:
                                                                                "row",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            overflow:
                                                                                "hidden",
                                                                            alignItems:
                                                                                "center",
                                                                        }}
                                                                    >
                                                                        {
                                                                            soRecord.qtyOrdered
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        fontWeight:
                                                                            "500",
                                                                        color: "#495057",
                                                                        backgroundColor:
                                                                            openedSoNumber ===
                                                                            soRecord.name
                                                                                ? "#a8d5f0ff"
                                                                                : "#fff",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display:
                                                                                "flex",
                                                                            flexDirection:
                                                                                "row",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            overflow:
                                                                                "hidden",
                                                                            alignItems:
                                                                                "center",
                                                                        }}
                                                                    >
                                                                        {
                                                                            soRecord.qtyShipped
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        fontWeight:
                                                                            "500",
                                                                        color: "#495057",
                                                                        backgroundColor:
                                                                            openedSoNumber ===
                                                                            soRecord.name
                                                                                ? "#a8d5f0ff"
                                                                                : "#fff",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display:
                                                                                "flex",
                                                                            flexDirection:
                                                                                "row",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            overflow:
                                                                                "hidden",
                                                                            alignItems:
                                                                                "center",
                                                                        }}
                                                                    >
                                                                        {
                                                                            soRecord.qtyReceived
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        color: "#202020ff",
                                                                        fontWeight:
                                                                            "700",
                                                                        backgroundColor:
                                                                            getNextStepColor(
                                                                                soRecord
                                                                                    .nextStep
                                                                                    ?.date_deadline
                                                                            ),
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        textDecoration:
                                                                            "underline",
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    {
                                                                        soRecord
                                                                            .nextStep
                                                                            ?.name || "No Next Action"
                                                                    }
                                                                </td>
                                                            </tr>
                                                            {soRecord.name ==
                                                                openedSoNumber && (
                                                                <tr>
                                                                    <td
                                                                        colSpan={
                                                                            5
                                                                        }
                                                                        style={{
                                                                            padding: 0,
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                overflow:
                                                                                    "visible",
                                                                                width: "100%",
                                                                            }}
                                                                        >
                                                                            {(() => {
                                                                                return soRecord
                                                                                    .products
                                                                                    .length >
                                                                                    0 ? (
                                                                                    <div
                                                                                        style={{
                                                                                            display:
                                                                                                "flex",
                                                                                            width: "100%",
                                                                                        }}
                                                                                    >
                                                                                        {/* Scrollable columns - reduced width */}
                                                                                        <div
                                                                                            className="scrollbar-hide"
                                                                                            style={{
                                                                                                width: "60%",
                                                                                            }}
                                                                                            ref={
                                                                                                leftScrollRef
                                                                                            }
                                                                                            onScroll={() =>
                                                                                                handleScroll(
                                                                                                    leftScrollRef,
                                                                                                    rightScrollRef
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <CustomerInfoTable
                                                                                                shippingInfoColumns={
                                                                                                    insideInfoColumns
                                                                                                }
                                                                                                products={
                                                                                                    soRecord.products
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                        <div
                                                                                            style={{
                                                                                                width: "60%",
                                                                                                height: 20,
                                                                                                backgroundColor:
                                                                                                    "#efefef",
                                                                                                paddingBottom: 35,
                                                                                            }}
                                                                                            ref={
                                                                                                rightScrollRef
                                                                                            }
                                                                                            onScroll={() =>
                                                                                                handleScroll(
                                                                                                    rightScrollRef,
                                                                                                    leftScrollRef
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <OrderStatusView
                                                                                                products={
                                                                                                    soRecord.products
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        style={{
                                                                                            height: "70vh",
                                                                                            display:
                                                                                                "flex",
                                                                                            flexDirection:
                                                                                                "row",
                                                                                            justifyContent:
                                                                                                "center",
                                                                                            alignItems:
                                                                                                "center",
                                                                                            fontSize: 16,
                                                                                            color: "#525252ff",
                                                                                        }}
                                                                                    >
                                                                                        No
                                                                                        Data
                                                                                        Found
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {tableView == "dashboard" && (
                                    <DeliveryDashboard
                                        startDate={currentStartDate}
                                        endDate={currentEndDate}
                                        dashboardData={dashboardData}
                                        delayedList={[]}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ColumnToggleDialog
                columns={shippingInfoColumns}
                setColumns={setShippingInfoColumns}
                saveColumnState={saveColumnStates}
            />

            <style>
                {`
                    .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                    }
                    .table-hover tbody tr:hover {
                    background-color: #f8f9fa;
                    }
                    
                    div[style*="overflowX: scroll"]::-webkit-scrollbar {
                    height: 8px;
                    }
                    
                    div[style*="overflowX: scroll"]::-webkit-scrollbar-track {
                    background: #f7fafc;
                    border-radius: 4px;
                    }
                    
                    div[style*="overflowX: scroll"]::-webkit-scrollbar-thumb {
                    background: #cbd5e0;
                    border-radius: 4px;
                    }
                    
                    div[style*="overflowX: scroll"]::-webkit-scrollbar-thumb:hover {
                    background: #a0aec0;
                    }
                `}
            </style>
        </div>
    );
};

export default DeliveryList;
